import { useState } from "react";
import { Link } from "react-router-dom";
import { track, trackCall, trackText, trackBook, trackIntake } from "@/analytics";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Loader2, FileDown, CheckCircle, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM",
];

const serviceOptions = [
  "Applied Kinesiology",
  "Extremity Adjustment",
  "Full Spine Chiropractic",
  "Initial Consultation",
  "K-Laser",
  "Trigenix",
];

export default function AppointmentForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", preferred_time: "", message: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [status, setStatus] = useState("idle");

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !date || !form.preferred_time) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    setStatus("idle");
    try {
      await axios.post(`${API}/appointments`, {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      setStatus("success");
      track("appointment_request", { service: form.service });
      toast.success("Appointment request submitted! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", service: "", preferred_time: "", message: "" });
      setDate(null);
    } catch {
      setStatus("error");
      toast.error("Something went wrong. Please call or text us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="appointment" className="py-24 lg:py-32 bg-[#F7F5F0]" data-testid="appointment-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left text */}
          <div>
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#C87B5D] mb-4">
              Get Started
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A221E] tracking-tight leading-none mb-8">
              Request an<br /> Appointment
            </h2>
            <p className="font-body text-base text-[#5C6A61] leading-relaxed mb-6 max-w-md">
              I'm available Tuesdays &amp; Thursdays at Earth Heart in Marion, NC, and on alternate Saturdays in Black Mountain. The fastest way to get a <strong className="text-[#1A221E]">confirmed</strong> Marion time is to book online — or send a request below and I'll confirm it with you by text.
            </p>
            <Link
              to="/book-online"
              className="inline-flex items-center gap-2 rounded-full bg-[#3A4F41] hover:bg-[#2d3e33] text-white font-body text-sm font-medium px-6 py-3 mb-8 transition-colors"
              onClick={trackBook}
              data-testid="appointment-book-online-cta"
            >
              <CalendarCheck size={16} />
              Book a confirmed time online
            </Link>
            <div className="bg-white rounded-3xl border border-[#E4DCCF] p-6 space-y-5" data-testid="locations-card">
              <div data-testid="location-marion">
                <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#C87B5D] mb-1">Location 1</p>
                <h4 className="font-heading text-lg font-medium text-[#3A4F41] mb-2"><Link to="/marion" className="hover:underline underline-offset-4" data-testid="location-marion-link">Marion</Link></h4>
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                  <strong className="text-[#1A221E]">Tuesday &amp; Thursday</strong><br />
                  10:00 AM – 12:00 PM · 3:00 PM – 6:00 PM<br />
                  Earth Heart Health Food Store<br />
                  4573 US 221 N, Marion, NC 28752<br />
                  <Link to="/book-online" onClick={trackBook} className="text-[#3A4F41] font-medium underline underline-offset-4" data-testid="location-marion-book-link">Book online</Link>
                  <span className="text-[#5C6A61]"> or call / text</span>
                </p>
              </div>
              <div className="border-t border-[#E4DCCF] pt-5" data-testid="location-black-mountain">
                <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#C87B5D] mb-1">Location 2</p>
                <h4 className="font-heading text-lg font-medium text-[#3A4F41] mb-2">Black Mountain</h4>
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                  <strong className="text-[#1A221E]">Alternate Saturdays</strong><br />
                  12:30 PM – 3:00 PM<br />
                  3174 US Hwy 70 W, Black Mountain, NC 28711<br />
                  <span className="italic text-[#C87B5D]">Phone bookings only — </span>
                  <a href="tel:+18286913551" onClick={trackCall("black_mountain")} className="text-[#C87B5D] hover:underline font-medium" data-testid="location-black-mountain-call">call</a>
                  <span className="text-[#C87B5D] italic"> or </span>
                  <a href="sms:+18286913551" onClick={trackText("black_mountain")} className="text-[#C87B5D] hover:underline font-medium" data-testid="location-black-mountain-text">text (828) 691-3551</a><br />
                  <Link to="/black-mountain" className="text-[#3A4F41] font-medium underline underline-offset-4" data-testid="location-black-mountain-link">About the Black Mountain location</Link>
                </p>
              </div>
              <div className="border-t border-[#E4DCCF] pt-5">
                <h4 className="font-heading text-lg font-medium text-[#3A4F41] mb-2">Appointments</h4>
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed flex flex-col gap-1">
                  <a href="tel:+18286913551" onClick={trackCall("marion")} className="text-[#C87B5D] hover:underline font-medium" data-testid="appointment-call-link">Call (828) 691-3551</a>
                  <a href="sms:+18286913551" onClick={trackText("marion")} className="text-[#C87B5D] hover:underline font-medium" data-testid="appointment-text-link">Text (828) 691-3551</a>
                  <span className="italic text-[#C87B5D]">By appointment only</span>
                </p>
              </div>
              <div>
                <h4 className="font-heading text-lg font-medium text-[#3A4F41] mb-2">New Patients</h4>
                <a
                  href="/intake-form.docx"
                  download="New-Patient-Intake-Form.docx"
                  onClick={trackIntake}
                  data-testid="download-intake-form"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body text-sm px-5 py-2.5 transition-colors"
                >
                  <FileDown size={16} />
                  Download Intake Form
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          {status === "success" ? (
            <div className="bg-white rounded-3xl border border-[#E4DCCF] p-8 lg:p-10 text-center" data-testid="appointment-success">
              <div className="w-16 h-16 rounded-full bg-[#3A4F41]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-[#3A4F41]" />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A221E] tracking-tight mb-3">
                Request Received
              </h3>
              <p className="font-body text-base text-[#5C6A61] leading-relaxed max-w-md mx-auto mb-4">
                Thank you — your request has been sent to Dr. Greenspan.
              </p>
              <p className="font-body text-sm text-[#1A221E] bg-[#C87B5D]/10 border border-[#C87B5D]/30 rounded-2xl px-4 py-3 max-w-md mx-auto mb-6" data-testid="appointment-success-notice">
                <strong>Please note:</strong> this is a request, not a confirmed booking. Your time is not reserved until Dr. Greenspan confirms it with you by text or phone. If you haven't heard back within one business day, please{" "}
                <a href="tel:+18286913551" className="text-[#C87B5D] font-medium hover:underline">call</a> or{" "}
                <a href="sms:+18286913551" className="text-[#C87B5D] font-medium hover:underline">text (828) 691-3551</a>.
              </p>
              <p className="font-body text-sm text-[#5C6A61] mb-8">
                Want it confirmed right now?{" "}
                <Link to="/book-online" className="text-[#3A4F41] font-medium underline underline-offset-4" data-testid="appointment-success-book-link">
                  Book a time online
                </Link>
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStatus("idle")}
                className="rounded-full border-[#E4DCCF] font-body text-sm"
                data-testid="appointment-send-another"
              >
                Send another request
              </Button>
            </div>
          ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl border border-[#E4DCCF] p-8 lg:p-10 space-y-5"
            data-testid="appointment-form"
          >
            <p className="font-body text-xs text-[#5C6A61] bg-[#F7F5F0] rounded-xl px-4 py-2.5" data-testid="appointment-form-notice">
              This form sends a <strong className="text-[#1A221E]">request</strong>. Your time is confirmed only when Dr. Greenspan texts or calls you back.
            </p>
            {status === "error" && (
              <div className="rounded-2xl border border-[#C87B5D]/40 bg-[#C87B5D]/10 p-4 font-body text-sm text-[#1A221E]" data-testid="appointment-error">
                <p className="font-medium mb-1">We couldn't send your request.</p>
                <p className="text-[#5C6A61]">
                  Please <a href="tel:+18286913551" className="text-[#C87B5D] font-medium hover:underline">call (828) 691-3551</a> or{" "}
                  <a href="sms:+18286913551" className="text-[#C87B5D] font-medium hover:underline">text us</a> and we'll get you scheduled.
                </p>
              </div>
            )}
            <div>
              <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Full Name *</label>
              <Input
                data-testid="appointment-name"
                placeholder="Your full name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="rounded-xl border-[#E4DCCF] bg-[#F7F5F0] h-11 font-body focus-visible:ring-[#3A4F41]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Email *</label>
                <Input
                  data-testid="appointment-email"
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="rounded-xl border-[#E4DCCF] bg-[#F7F5F0] h-11 font-body focus-visible:ring-[#3A4F41]"
                />
              </div>
              <div>
                <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Phone *</label>
                <Input
                  data-testid="appointment-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="rounded-xl border-[#E4DCCF] bg-[#F7F5F0] h-11 font-body focus-visible:ring-[#3A4F41]"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Service *</label>
              <Select value={form.service} onValueChange={(v) => handleChange("service", v)}>
                <SelectTrigger
                  data-testid="appointment-service"
                  className="rounded-xl border-[#E4DCCF] bg-[#F7F5F0] h-11 font-body focus:ring-[#3A4F41]"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((s) => (
                    <SelectItem key={s} value={s} className="font-body">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Preferred Date *</label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="appointment-date-trigger"
                      variant="outline"
                      className="w-full rounded-xl border-[#E4DCCF] bg-[#F7F5F0] h-11 font-body justify-start text-left font-normal hover:bg-[#F7F5F0] focus:ring-[#3A4F41]"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#5C6A61]" />
                      {date ? (
                        <span className="text-[#1A221E]">{format(date, "PPP")}</span>
                      ) : (
                        <span className="text-[#5C6A61]">Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      data-testid="appointment-calendar"
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                      disabled={(d) => d < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Preferred Time *</label>
                <Select value={form.preferred_time} onValueChange={(v) => handleChange("preferred_time", v)}>
                  <SelectTrigger
                    data-testid="appointment-time"
                    className="rounded-xl border-[#E4DCCF] bg-[#F7F5F0] h-11 font-body focus:ring-[#3A4F41]"
                  >
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((t) => (
                      <SelectItem key={t} value={t} className="font-body">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="font-body text-sm text-[#1A221E] font-medium mb-1.5 block">Message (optional)</label>
              <Textarea
                data-testid="appointment-message"
                placeholder="Anything you'd like us to know..."
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                className="rounded-xl border-[#E4DCCF] bg-[#F7F5F0] min-h-[100px] font-body focus-visible:ring-[#3A4F41]"
              />
            </div>

            <Button
              data-testid="appointment-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body text-sm h-12"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Send Appointment Request"
              )}
            </Button>
          </form>
          )}
        </div>
      </div>
    </section>
  );
}
